import { useEffect, useMemo, useState } from 'react'
import { Olympiad, OlympiadStatus, olympiadStatusFilterOrder } from './models/Olympiad'
import { OlympiadsApi } from 'api'

const buildStatusesKey = (statuses: readonly OlympiadStatus[]) =>
  olympiadStatusFilterOrder.filter((status) => statuses.includes(status)).join(',')

const buildYearsKey = (years: readonly string[]) => years.join(',')

export const useOlympiadsFormControl = (
  statuses: readonly OlympiadStatus[],
  years: readonly string[],
  enabled = true,
) => {
  const [olympiads, setOlympiads] = useState<Olympiad[]>([])
  const [loading, setLoading] = useState(enabled)
  const statusesKey = useMemo(() => buildStatusesKey(statuses), [statuses])
  const yearsKey = useMemo(() => buildYearsKey(years), [years])

  useEffect(() => {
    if (!enabled) {
      setOlympiads([])
      setLoading(false)
      return
    }

    let cancelled = false
    const requestStatuses = [...statuses]
    const requestYears = [...years]

    const getOlympiads = async () => {
      setLoading(true)

      try {
        const data = await OlympiadsApi.getUnAuthOlympiads(requestStatuses, requestYears)
        if (!cancelled) {
          setOlympiads(
            Array.isArray(data)
              ? data.map((item) => ({
                  ...item,
                  status: item.status ?? OlympiadStatus.future,
                }))
              : []
          )
        }
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          setOlympiads([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    getOlympiads()

    return () => {
      cancelled = true
    }
  }, [enabled, statusesKey, yearsKey])

  return {
    olympiads,
    loading,
  }
}
